import React, { useState, useEffect } from "react";
import { Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Typography from "@mui/material/Typography";
import ButtonMD from "../../components/Items/Button";
import TypographyMD from "../../components/Items/Typography";
import CardMD from "../../components/Items/CardComponent";
import LoadingBar from "react-top-loading-bar";
import { tokens } from "../../theme";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import toastAlert from "../../components/Items/ToastReact";
import { loginWithGoogle, checkAuthSession } from "../../app/features/auth";
import { activityLogs } from "../../app/features/activity_logs";
import { onAuthStateChange, storeUserData } from "../../services/supabaseAuth";

function Login() {
  console.log("🟢 Login: Component rendering");
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();

  // Location tracking removed - not used in production
  const { isLoading, user, session } = useSelector((state) => state.auth);
  const [progress, setProgress] = useState(0);

  const handleGoogleSignIn = async () => {
    console.log("🟢 Login: handleGoogleSignIn called - button clicked!");
    try {
      console.log("🟢 Login: Setting progress to 30");
      setProgress(30);
      console.log("🟢 Login: Dispatching loginWithGoogle...");
      const result = await dispatch(loginWithGoogle()).unwrap();
      console.log("🟢 Login: loginWithGoogle result:", result);
      setProgress(100);
      // The OAuth flow will redirect, so we don't need to navigate here
      console.log("🟢 Login: OAuth redirect should happen now");
    } catch (error) {
      console.error("❌ Login: Google sign in error:", error);
      setProgress(0);
      toastAlert("error", error?.message || "Failed to sign in with Google");
    }
  };

  // Redirect if user is already logged in
  useEffect(() => {
    if (session && user) {
      console.log("🟢 Login: User already has session, redirecting to home");
      navigate("/", { replace: true });
    }
  }, [session, user, navigate]);

  // Handle OAuth callback - only listen for auth state changes
  // App.js already checks the session on mount, so we don't need to check it here
  useEffect(() => {
    console.log("🟢 Login: useEffect started - setting up auth listener only");
    let isMounted = true;

    // Listen for auth state changes (OAuth callback)
    console.log("🟢 Login: Setting up auth state listener");
    const {
      data: { subscription },
    } = onAuthStateChange(async (event, session) => {
      if (!isMounted) return;

      console.log(
        "🟢 Login: Auth state changed:",
        event,
        session ? "session exists" : "no session"
      );

      if (event === "SIGNED_IN" && session?.user) {
        console.log("🟢 Login: User signed in via OAuth callback");
        // Update Redux state
        await dispatch(checkAuthSession()).unwrap();

        // Store user data
        const userData = await storeUserData(session.user);

        if (userData.data && isMounted) {
          const activityData = {
            user_id: session.user.id,
            event_key: "LOGIN",
            event_action_name: "LOGGED IN",
            ip_address: "N/A",
            ip_country: "Unknown",
            date: new Date().toISOString(),
          };

          try {
            await dispatch(activityLogs(activityData)).unwrap();
          } catch (logError) {
            console.error("❌ Login: Error logging activity:", logError);
          }

          if (isMounted) {
            navigate("/");
          }
        }
      } else if (event === "SIGNED_OUT") {
        console.log("🟢 Login: User signed out");
        // Navigation will be handled by App.js
      }
    });

    return () => {
      console.log("🟢 Login: Cleaning up useEffect");
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [dispatch, navigate]);

  console.log("🟢 Login: Rendering JSX - isLoading:", isLoading);
  return (
    <>
      <>
        <div
          style={{
            backgroundColor: `${colors.primary[500]}`,
            height: "100vh",
            display: "flex", // FLEX!
            justifyContent: "center", // horizontal center
            alignItems: "center", // vertical center
            flexDirection: "column", // to maintain top-to-bottom stacking
          }}
        >
          <Grid container>
            <Grid item xs={12} md={12}>
              <LoadingBar
                color={`${colors.primary[100]}`}
                progress={progress}
                onLoaderFinished={() => setProgress(0)}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={12}
              align="center"
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "2em",
              }}
            >
              <CardMD
                maxWidth={470}
                borderRadius={5}
                ContentData={
                  <>
                    <Typography
                      variant="h3"
                      gutterBottom
                      textAlign="center"
                      style={{
                        fontWeight: 600,
                        color: `${colors.primary[100]}`,
                        marginBottom: "2rem",
                      }}
                    >
                      SIGN IN
                    </Typography>
                    <div
                      style={{
                        marginLeft: "20px",
                        marginRight: "20px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <TypographyMD
                        marginBottom="20px"
                        variant="body1"
                        label="Sign in with your Google account"
                        color={`${colors.primary[600]}`}
                        align="center"
                      />
                      <ButtonMD
                        title={
                          isLoading ? "Signing in..." : "Sign in with Google"
                        }
                        onClickTerm={handleGoogleSignIn}
                        disabled={isLoading}
                        style={{
                          width: "100%",
                          maxWidth: "300px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "10px",
                        }}
                      />
                    </div>
                  </>
                }
              />
            </Grid>
          </Grid>
        </div>
      </>
    </>
  );
}

export default Login;
